import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DigitalSignApprover } from "domain/entities/digital-sign-approver";
import { DigitalSignApproverInputFilter } from "domain/models/digital-sign-approver-input-filter";
import { Logger } from "infra/logger/logger";
import repository from "../../infra/repositories/digital-sign-approver-repository";

export const useDigitalSignApproverByFilter = (
  filter: DigitalSignApproverInputFilter
) => {
  const logger = Logger.getInstance();
  return useQuery({
    queryKey: ["digitalSignApprovers"],
    queryFn: async () => {
      try {
        const result = await repository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
    enabled: !!filter.referenceId,
  });
};

export const useDigitalSignApproversById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["digitalSignApprover", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("DigitalSignApprover ID inválido");
        const result = await repository.getById(id);

        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreateDigitalSignApproverMutation = () => {
  const logger = Logger.getInstance();
  const queryDigitalSigns = useQueryClient();

  return useMutation({
    mutationFn: async (data: DigitalSignApprover) => {
      const result = await repository.create(data);
      return result;
    },
    onSuccess: (data) => {
      queryDigitalSigns.invalidateQueries({
        queryKey: ["digitalSignApprovers", {}],
      });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);

          // Cria um objeto de erro com o array de erros como propriedade
          const customError = new Error("Erro na criação da client");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
          throw new Error("Erro desconhecido da API");
        }
      } else {
        logger.error("Erro desconhecido", error);
        throw error;
      }
    },
  });
};
